import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

/**
 * Shared grouping view state for any grouped deals view (pipeline, deal list …): collapse/expand of groups plus
 * Collapse All / Expand All. `stages` is optional metadata used by the pipeline's "Deal Stages" settings tab;
 * views that don't hide stages simply never call `registerStages`.
 *
 * Model: we track the set of EXPANDED groups, so the default (empty set) is "everything collapsed". This makes
 * newly appearing groups (grouping turned on / dimension changed) render collapsed synchronously — no post-render
 * effect, so no expand→collapse flicker.
 */
export interface GroupingStageMeta {
  id: number;
  title: string;
}

interface GroupingViewContextValue {
  /** Whether a group key (see `groupKeyPath`) is collapsed — computed during render, collapsed by default. */
  isCollapsed: (key: string) => boolean;
  toggleGroup: (key: string) => void;
  collapseAll: () => void;
  expandAll: () => void;
  /** The view reports every group key it currently renders so Expand All can target them. */
  registerGroupKeys: (keys: string[]) => void;
  /** Optional: hideable stages the view renders — powers the pipeline's Deal Stages settings tab. */
  stages: GroupingStageMeta[];
  registerStages: (stages: GroupingStageMeta[]) => void;
}

const GroupingViewContext = createContext<GroupingViewContextValue | null>(null);

export const GroupingViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [stages, setStages] = useState<GroupingStageMeta[]>([]);
  const allKeysRef = useRef<string[]>([]);

  const registerGroupKeys = useCallback((keys: string[]) => {
    allKeysRef.current = keys;
  }, []);

  const registerStages = useCallback((next: GroupingStageMeta[]) => {
    setStages((prev) => {
      if (
        prev.length === next.length &&
        prev.every((stage, index) => stage.id === next[index].id && stage.title === next[index].title)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => setExpandedGroups(new Set()), []);
  const expandAll = useCallback(() => setExpandedGroups(new Set(allKeysRef.current)), []);
  const isCollapsed = useCallback((key: string) => !expandedGroups.has(key), [expandedGroups]);

  const value = useMemo<GroupingViewContextValue>(
    () => ({ isCollapsed, toggleGroup, collapseAll, expandAll, registerGroupKeys, stages, registerStages }),
    [isCollapsed, toggleGroup, collapseAll, expandAll, registerGroupKeys, stages, registerStages]
  );

  return <GroupingViewContext.Provider value={value}>{children}</GroupingViewContext.Provider>;
};

/** Safe to call outside the provider — returns inert no-ops so components never crash. */
export const useGroupingView = (): GroupingViewContextValue => {
  const ctx = useContext(GroupingViewContext);
  if (!ctx) {
    return {
      isCollapsed: () => false,
      toggleGroup: () => undefined,
      collapseAll: () => undefined,
      expandAll: () => undefined,
      registerGroupKeys: () => undefined,
      stages: [],
      registerStages: () => undefined
    };
  }
  return ctx;
};
